import { useState, type ReactElement } from 'react';
import { useI18n } from '../../lib/i18n/I18nProvider';
import { newIntent, useApiAction, useApiList } from '../../lib/api/queries';
import type {
  AttributionException,
  CustomerNote,
  Invitation,
  LeadRow,
  PendingLead,
  PublicLink,
  TimelineEntry,
} from '../../lib/api/types';
import { Badge, Card, DataTable, Field, Ltr, PageTitle, Select, TextArea } from '../../ui/primitives';
import { DegradedState, EmptyState, ErrorState, LoadingState, StateIcon, SuccessNote } from '../../ui/states';
import { AuditStatement, RefusalWithRemedy } from '../../ui/guards';
import { statusTone } from '../admin/Expos';

const ASSIGN_REASONS = [
  'campaign_assignment',
  'broker_offboarded',
  'manual_reassignment',
  'customer_request',
  'administrative_error',
];

/** `BRK-04` — invitations through pending → sent → opened → accepted, plus the failure states. */
export function BrokerageInvitations(): ReactElement {
  const { t, formatDateTime } = useI18n();
  const path = '/v1/brokerage/invitations';
  const invitations = useApiList<Invitation>(path);
  const create = useApiAction<Invitation>([path]);
  const action = useApiAction<Invitation>([path]);
  const [phone, setPhone] = useState('');
  const [expoId, setExpoId] = useState('');

  return (
    <>
      <PageTitle title={t('brk.invitations.title')}>{t('brk.invitations.desc')}</PageTitle>
      <Card title={t('brk.invitations.title')}>
        {invitations.isLoading ? <LoadingState /> : null}
        {invitations.error ? <ErrorState error={invitations.error} onRetry={() => void invitations.refetch()} /> : null}
        {invitations.data ? (
          <DataTable<Invitation>
            caption={t('brk.invitations.title')}
            rows={invitations.data.items ?? []}
            rowKey={(i) => i.ID}
            empty={<EmptyState>{t('brk.invitations.desc')}</EmptyState>}
            columns={[
              { key: 'code', header: t('table.code'), cell: (i) => <Ltr>{i.Code}</Ltr> },
              {
                key: 'status',
                header: t('table.status'),
                cell: (i) => <Badge tone={statusTone(i.Status)}>{i.Status}</Badge>,
              },
              { key: 'expires', header: 'ExpiresAt', cell: (i) => formatDateTime(i.ExpiresAt) },
              {
                key: 'actions',
                header: t('table.actions'),
                cell: (i) => (
                  <span className="row">
                    {['send', 'revoke'].map((verb) => (
                      <button
                        key={verb}
                        type="button"
                        className={verb === 'revoke' ? 'btn btn--destructive btn--small' : 'btn btn--small'}
                        disabled={action.isPending}
                        onClick={() => action.mutate(newIntent({ path: `${path}/${i.ID}:${verb}`, body: {} }))}
                      >
                        {verb}
                      </button>
                    ))}
                  </span>
                ),
              },
            ]}
          />
        ) : null}
        {action.error ? <ErrorState error={action.error} /> : null}
      </Card>

      <Card title={t('action.create')}>
        <form
          onSubmit={(ev) => {
            ev.preventDefault();
            create.mutate(newIntent({ path, body: { intended_phone_e164: phone, expo_id: expoId } }));
          }}
        >
          <Field label="intended_phone_e164" name="inv-phone" type="tel" value={phone} onChange={setPhone} ltr required />
          <Field label="expo_id" name="inv-expo" value={expoId} onChange={setExpoId} ltr />
          {create.error ? <ErrorState error={create.error} /> : null}
          <button className="btn" type="submit" disabled={create.isPending}>
            {t('action.create')}
          </button>
        </form>
      </Card>
    </>
  );
}

/** `BRK-05` — campaign links. */
export function BrokerageLinks(): ReactElement {
  const { t } = useI18n();
  const path = '/v1/brokerage/public-links';
  const links = useApiList<PublicLink>(path);
  const create = useApiAction<PublicLink>([path]);
  const patch = useApiAction<PublicLink>([path]);
  const [slug, setSlug] = useState('');
  const [campaign, setCampaign] = useState('');
  const [channel, setChannel] = useState('whatsapp');
  const [expoId, setExpoId] = useState('');

  return (
    <>
      <PageTitle title={t('brk.links.title')}>{t('brk.links.desc')}</PageTitle>
      <Card title={t('brk.links.title')}>
        {links.isLoading ? <LoadingState /> : null}
        {links.error ? <ErrorState error={links.error} onRetry={() => void links.refetch()} /> : null}
        {links.data ? (
          <DataTable<PublicLink>
            caption={t('brk.links.title')}
            rows={links.data.items ?? []}
            rowKey={(l) => l.ID}
            empty={<EmptyState>{t('brk.links.desc')}</EmptyState>}
            columns={[
              { key: 'slug', header: 'slug', cell: (l) => <Ltr>{l.Slug}</Ltr> },
              { key: 'campaign', header: 'campaign_name', cell: (l) => l.CampaignName },
              { key: 'channel', header: 'channel', cell: (l) => <Ltr>{l.Channel}</Ltr> },
              {
                key: 'status',
                header: t('table.status'),
                cell: (l) => <Badge tone={statusTone(l.Status)}>{l.Status}</Badge>,
              },
              {
                key: 'actions',
                header: t('table.actions'),
                cell: (l) => (
                  <button
                    type="button"
                    className="btn btn--destructive btn--small"
                    disabled={patch.isPending || l.Status === 'revoked'}
                    onClick={() =>
                      patch.mutate(newIntent({
                        path: `${path}/${l.ID}`,
                        method: 'PATCH',
                        body: { status: 'revoked' },
                        ifMatch: l.Version,
                      }))
                    }
                  >
                    revoke
                  </button>
                ),
              },
            ]}
          />
        ) : null}
        {patch.error ? <ErrorState error={patch.error} /> : null}
      </Card>

      <Card title={t('action.create')}>
        <form
          onSubmit={(ev) => {
            ev.preventDefault();
            create.mutate(newIntent({
              path,
              body: {
                slug,
                campaign_name: campaign,
                channel,
                utm: { utm_source: channel, utm_campaign: campaign },
                expo_id: expoId,
              },
            }));
          }}
        >
          <Field label="slug" name="link-slug" value={slug} onChange={setSlug} required ltr />
          <Field label="campaign_name" name="link-campaign" value={campaign} onChange={setCampaign} required />
          <Field label="channel" name="link-channel" value={channel} onChange={setChannel} required ltr />
          <Field label="expo_id" name="link-expo" value={expoId} onChange={setExpoId} ltr />
          {create.error ? <ErrorState error={create.error} /> : null}
          <button className="btn" type="submit" disabled={create.isPending}>
            {t('action.create')}
          </button>
        </form>
      </Card>
    </>
  );
}

/** `BRK-07`/`BRK-09` the roster · `BRK-08` assignment. */
export function BrokerageLeads(): ReactElement {
  const { t } = useI18n();
  const [expoId, setExpoId] = useState('');
  const [unassigned, setUnassigned] = useState(false);
  const query: Record<string, string> = {};
  if (expoId) query['expo_id'] = expoId;
  if (unassigned) query['unassigned'] = 'true';
  const leads = useApiList<LeadRow>('/v1/brokerage/leads', query);
  const assign = useApiAction(['/v1/brokerage/leads']);
  const [broker, setBroker] = useState('');
  const [reason, setReason] = useState(ASSIGN_REASONS[0] ?? 'campaign_assignment');
  // THE SELECTION IS A (customer, expo) PAIR, NOT A CUSTOMER (P13-f H7).
  //
  // `crm.Reassign` takes `ExpoID`, and an empty one is not "this expo" — it is every expo. Keeping
  // only the customer id meant the screen could close and reopen ownership intervals across expos
  // the operator was not looking at, from a row that named exactly one.
  const [selected, setSelected] = useState<LeadRow | null>(null);

  return (
    <>
      <PageTitle title={t('brk.leads.title')}>{t('brk.leads.desc')}</PageTitle>
      <Card title={t('brk.leads.title')}>
        <Field label="expo_id" name="lead-expo" value={expoId} onChange={setExpoId} ltr />
        <div className="field">
          <label htmlFor="unassigned-only">{t('brk.unassigned')}</label>
          <input
            id="unassigned-only"
            type="checkbox"
            checked={unassigned}
            onChange={(e) => setUnassigned(e.target.checked)}
            style={{ width: 'auto', minHeight: 'auto' }}
          />
        </div>
        {leads.isLoading ? <LoadingState /> : null}
        {leads.error ? <ErrorState error={leads.error} onRetry={() => void leads.refetch()} /> : null}
        {leads.data ? (
          <DataTable<LeadRow>
            caption={t('brk.leads.title')}
            rows={leads.data.items ?? []}
            rowKey={(l) => `${l.customer_id}|${l.expo_id}`}
            empty={<EmptyState>{t('brk.leads.desc')}</EmptyState>}
            columns={[
              {
                key: 'customer',
                header: t('table.identifier'),
                cell: (l) => (
                  <button
                    type="button"
                    className="btn btn--secondary btn--small"
                    onClick={() => setSelected(l)}
                  >
                    <Ltr>{l.customer_id.slice(0, 8)}</Ltr>
                  </button>
                ),
              },
              { key: 'phone', header: 'phone_masked', cell: (l) => <Ltr>{l.phone_masked}</Ltr> },
              { key: 'source', header: 'source_type', cell: (l) => <Ltr>{l.source_type}</Ltr> },
              {
                key: 'broker',
                header: 'broker_user_id',
                cell: (l) => (l.broker_user_id ? <Ltr>{l.broker_user_id.slice(0, 8)}</Ltr> : t('table.none')),
              },
            ]}
          />
        ) : null}
      </Card>

      {selected ? (
        <>
          <Card title={`${t('brk.customer.title')} · ${selected.customer_id.slice(0, 8)}`}>
            <AuditStatement>
              <p>
                <Ltr>lead.assign</Ltr> — the previous ownership interval closes atomically as the new
                one opens, with a mandatory reason code. An open attribution exception freezes nothing
                and MUST NOT block this.
              </p>
            </AuditStatement>
            <Field label="to_broker_user_id" name="assign-broker" value={broker} onChange={setBroker} ltr required />
            <Select
              label={t('guard.reason')}
              name="assign-reason"
              value={reason}
              options={ASSIGN_REASONS.map((r) => ({ value: r, label: r }))}
              onChange={setReason}
            />
            <p className="muted">
              <Ltr>expo_id={selected.expo_id}</Ltr>
            </p>
            {assign.error ? (
              <RefusalWithRemedy error={assign.error} action="lead.assign" retryExact={assign.retryLast} />
            ) : null}
            <div className="row">
              {['assign', 'reassign'].map((verb) => (
                <button
                  key={verb}
                  type="button"
                  className="btn"
                  disabled={assign.isPending || broker === '' || !selected.expo_id}
                  onClick={() =>
                    assign.mutate(newIntent({
                      path: `/v1/brokerage/leads/${selected.customer_id}:${verb}`,
                      label: `${verb} ${selected.customer_id}`,
                      // The decoder is strict and the field is `reason`; `reason_code` was an
                      // unknown field and every assignment was refused before it reached the domain.
                      body: {
                        to_broker_user_id: broker,
                        reason,
                        expo_id: selected.expo_id,
                      },
                    }))
                  }
                >
                  {verb}
                </button>
              ))}
            </div>
          </Card>
          <BrokerageCustomer customerId={selected.customer_id} />
        </>
      ) : null}
    </>
  );
}

function BrokerageCustomer({ customerId }: { customerId: string }): ReactElement {
  const { t, formatDateTime } = useI18n();
  const timeline = useApiList<TimelineEntry>(`/v1/brokerage/customers/${customerId}/attribution-timeline`);
  const notes = useApiList<CustomerNote>(`/v1/brokerage/customers/${customerId}/notes`);
  const addNote = useApiAction<CustomerNote>([`/v1/brokerage/customers/${customerId}/notes`]);
  const [body, setBody] = useState('');

  return (
    <>
      <Card title={t('brk.timeline.title')} description={t('brk.timeline.desc')}>
        {timeline.isLoading ? <LoadingState /> : null}
        {timeline.error ? <ErrorState error={timeline.error} onRetry={() => void timeline.refetch()} /> : null}
        {timeline.data ? (
          <DataTable<TimelineEntry>
            caption={t('brk.timeline.title')}
            rows={timeline.data.items ?? []}
            rowKey={(e) => `${e.at}|${e.kind}`}
            empty={<EmptyState />}
            columns={[
              { key: 'at', header: t('table.created'), cell: (e) => formatDateTime(e.at) },
              { key: 'kind', header: t('table.kind'), cell: (e) => <Ltr>{e.kind}</Ltr> },
              { key: 'detail', header: 'detail', cell: (e) => <Ltr>{JSON.stringify(e.detail)}</Ltr> },
            ]}
          />
        ) : null}
      </Card>

      <Card title={t('brk.notes.title')}>
        {notes.error ? <ErrorState error={notes.error} /> : null}
        {notes.data ? (
          <DataTable<CustomerNote>
            caption={t('brk.notes.title')}
            rows={notes.data.items ?? []}
            rowKey={(n) => n.id}
            empty={<EmptyState />}
            columns={[
              { key: 'created', header: t('table.created'), cell: (n) => formatDateTime(n.created_at) },
              { key: 'body', header: 'body', cell: (n) => n.body },
            ]}
          />
        ) : null}
        <TextArea label="body" name="note-body" value={body} onChange={setBody} />
        {addNote.error ? <ErrorState error={addNote.error} /> : null}
        <button
          className="btn"
          type="button"
          disabled={addNote.isPending || body.trim() === ''}
          onClick={() =>
            addNote.mutate(
              newIntent({ path: `/v1/brokerage/customers/${customerId}/notes`, body: { body } }),
              { onSuccess: () => setBody('') },
            )
          }
        >
          {t('action.create')}
        </button>
      </Card>
    </>
  );
}

/**
 * `crm.BulkReassignResult`, as the route actually renders it (round-2 M10).
 *
 * Three things were wrong with the previous shape, and each of them made this screen lie:
 *
 *   - the per-row success status is `reassigned`, not `ok` — `reassignOneItem` sets exactly those
 *     two words — so EVERY successful row matched `status !== 'ok'` and was listed as a failure. A
 *     clean 500-lead move rendered five hundred failures;
 *   - a failed row carries `code` and `detail`; there is no `reason` field on `ReassignResult`, so
 *     the one place a real refusal could have been explained rendered an empty string;
 *   - the envelope carries its own `status` — `completed` or `partially_completed` — which is the
 *     server's own summary of the batch and was thrown away.
 */
interface ReassignRow {
  customer_id: string;
  /** `reassigned` when the move committed, `failed` otherwise. */
  status: string;
  ownership_id?: string;
  code?: string;
  detail?: string;
}

interface BulkResult {
  bulk_operation_id?: string;
  /** `completed` or `partially_completed`. */
  status?: string;
  results?: ReassignRow[];
}

const REASSIGNED = 'reassigned';

/** `BRK-20` — the pending-reassignment queue, single and bulk. */
export function BrokeragePendingReassignment(): ReactElement {
  const { t, formatDateTime } = useI18n();
  const [expoId, setExpoId] = useState('');
  const query: Record<string, string> = {};
  if (expoId) query['expo_id'] = expoId;
  const pending = useApiList<PendingLead>('/v1/brokerage/leads/pending-reassignment', query);
  const bulk = useApiAction<BulkResult>(['/v1/brokerage/leads/pending-reassignment']);
  const single = useApiAction(['/v1/brokerage/leads/pending-reassignment']);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [broker, setBroker] = useState('');

  const items = pending.data?.items ?? [];
  const toggle = (id: string): void => {
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <>
      <PageTitle title={t('brk.pending.title')}>{t('brk.pending.desc')}</PageTitle>
      <Card title={t('brk.pending.title')}>
        <Field label="expo_id" name="pending-expo" value={expoId} onChange={setExpoId} ltr />
        {pending.isLoading ? <LoadingState /> : null}
        {pending.error ? <ErrorState error={pending.error} onRetry={() => void pending.refetch()} /> : null}
        {pending.data ? (
          <DataTable<PendingLead>
            caption={t('brk.pending.title')}
            rows={items}
            rowKey={(p) => `${p.customer_id}|${p.expo_id}`}
            empty={<EmptyState title={t('brk.pending.empty')}>{t('brk.pending.desc')}</EmptyState>}
            columns={[
              {
                key: 'select',
                header: t('action.filter'),
                cell: (p) => (
                  <input
                    type="checkbox"
                    aria-label={p.customer_id}
                    checked={selected.has(p.customer_id)}
                    onChange={() => toggle(p.customer_id)}
                    style={{ width: 'auto', minHeight: 'auto' }}
                  />
                ),
              },
              { key: 'customer', header: t('table.identifier'), cell: (p) => <Ltr>{p.customer_id.slice(0, 8)}</Ltr> },
              {
                key: 'former',
                header: 'former_broker_user_id',
                cell: (p) => (p.former_broker_user_id ? <Ltr>{p.former_broker_user_id.slice(0, 8)}</Ltr> : t('table.none')),
              },
              {
                key: 'waiting',
                header: 'waiting_since',
                cell: (p) => formatDateTime(new Date(p.waiting_since * 1000).toISOString()),
              },
              {
                key: 'actions',
                header: t('table.actions'),
                cell: (p) => (
                  <button
                    type="button"
                    className="btn btn--small"
                    disabled={single.isPending || broker === ''}
                    onClick={() =>
                      single.mutate(newIntent({
                        path: `/v1/brokerage/leads/${p.customer_id}:reassign`,
                        body: {
                          to_broker_user_id: broker,
                          reason: 'broker_offboarded',
                          expo_id: p.expo_id,
                        },
                      }))
                    }
                  >
                    reassign
                  </button>
                ),
              },
            ]}
          />
        ) : null}
        {single.error ? <ErrorState error={single.error} /> : null}
      </Card>

      <Card title={t('brk.bulk.title')} description={t('brk.bulk.desc')}>
        <AuditStatement>
          <p>
            <Ltr>lead.reassign_bulk</Ltr> — Brokerage Admin only, at most 500 ids, per-customer
            results, and a replay returns the identical <Ltr>bulk_operation_id</Ltr>.
          </p>
        </AuditStatement>
        <Field label="to_broker_user_id" name="bulk-broker" value={broker} onChange={setBroker} ltr required />
        {/* An empty `expo_id` broadens a bulk reassignment across every expo; it is required here. */}
        <Field label="expo_id" name="bulk-expo" value={expoId} onChange={setExpoId} ltr required />
        <p className="muted numeral">{selected.size} / 500</p>
        {bulk.error ? <ErrorState error={bulk.error} /> : null}
        {bulk.data?.bulk_operation_id ? (
          <SuccessNote>
            <Ltr>
              bulk_operation_id={bulk.data.bulk_operation_id} · {bulk.data.status ?? ''} ·{' '}
              {(bulk.data.results ?? []).filter((r) => r.status === REASSIGNED).length}/
              {(bulk.data.results ?? []).length} reassigned
            </Ltr>
          </SuccessNote>
        ) : null}
        {/* A BULK MOVE IS NOT ALL-OR-NOTHING (doc 06 §5.4.1), so the rows that did NOT move are the
            ones worth listing — and only those. The server's own `code` and `detail` say why. */}
        {bulk.data?.results?.some((r) => r.status !== REASSIGNED) ? (
          <DegradedState title="customers that did not move">
            <ul data-testid="bulk-failures">
              {bulk.data.results
                .filter((r) => r.status !== REASSIGNED)
                .map((r) => (
                  <li key={r.customer_id} className="ltr">
                    {r.customer_id}: {r.status}
                    {r.code ? ` · ${r.code}` : ''}
                    {r.detail ? ` · ${r.detail}` : ''}
                  </li>
                ))}
            </ul>
          </DegradedState>
        ) : null}
        <button
          className="btn"
          type="button"
          disabled={
            bulk.isPending || selected.size === 0 || selected.size > 500 || broker === '' || expoId === ''
          }
          onClick={() =>
            bulk.mutate(newIntent({
              path: '/v1/brokerage/leads:reassign-bulk',
              // `handleBulkReassign` decodes exactly these three fields, strictly; the reason is
              // the server's own (`reassign_workload`) and naming one here was a refusal.
              body: {
                customer_ids: Array.from(selected),
                to_broker_user_id: broker,
                expo_id: expoId,
              },
            }))
          }
        >
          {t('brk.bulk.title')}
        </button>
      </Card>
    </>
  );
}

/** `BRK-13` — raising an exceptional-resolution request. */
export function BrokerageExceptions(): ReactElement {
  const { t } = useI18n();
  const path = '/v1/brokerage/attribution-exceptions';
  const list = useApiList<AttributionException>(path);
  const raise = useApiAction<AttributionException>([path]);
  const [customerId, setCustomerId] = useState('');
  const [category, setCategory] = useState('administrative_error');
  const [summary, setSummary] = useState('');

  return (
    <>
      <PageTitle title={t('admin.exceptions.title')}>{t('brk.exceptions.desc')}</PageTitle>
      <Card title={t('admin.exceptions.title')}>
        {list.isLoading ? <LoadingState /> : null}
        {list.error ? <ErrorState error={list.error} onRetry={() => void list.refetch()} /> : null}
        {list.data ? (
          <DataTable<AttributionException>
            caption={t('admin.exceptions.title')}
            rows={list.data.items ?? []}
            rowKey={(e) => e.id}
            empty={<EmptyState>{t('brk.exceptions.empty')}</EmptyState>}
            columns={[
              { key: 'id', header: t('table.identifier'), cell: (e) => <Ltr>{e.id.slice(0, 8)}</Ltr> },
              { key: 'category', header: 'category', cell: (e) => <Ltr>{e.category}</Ltr> },
              {
                key: 'status',
                header: t('table.status'),
                cell: (e) => <Badge tone={statusTone(e.status)}>{e.status}</Badge>,
              },
              { key: 'summary', header: 'summary', cell: (e) => e.summary ?? t('table.none') },
            ]}
          />
        ) : null}
      </Card>

      <Card title={t('action.create')}>
        {/*
          THE SCREEN STATES FIRST THAT THE SYSTEM RECORD IS AUTHORITATIVE — and then, once, what
          actually decides the exception the brokerage is about to raise (round-4 A3/B1).

          The same paragraph used to be printed here for the third time on one screen, and what it
          said was no longer true: it described the resolution as a named attestation that creates
          and consumes no approval record. `AdminExceptions` now performs a real one-use
          `iam_approvals` hand-off, so this surface — which only DESCRIBES that flow — states the
          recorded decision instead.
        */}
        <div className="state state--locked" data-testid="brk-exception-resolution">
          <p className="state__title">
            <StateIcon kind="locked" />
            {t('admin.exceptions.title')}
          </p>
          <p>{t('brk.exceptions.resolution')}</p>
        </div>
        <Field label="customer_id" name="ex-customer" value={customerId} onChange={setCustomerId} ltr required />
        <Select
          label="category"
          name="ex-category"
          value={category}
          options={['administrative_error', 'identity_merge_split', 'fraud', 'data_integrity'].map((c) => ({
            value: c,
            label: c,
          }))}
          onChange={setCategory}
        />
        <TextArea label="summary" name="ex-summary" value={summary} onChange={setSummary} required />
        {raise.error ? <ErrorState error={raise.error} /> : null}
        <button
          className="btn"
          type="button"
          disabled={raise.isPending || customerId === ''}
          onClick={() =>
            raise.mutate(newIntent({
              path,
              body: { customer_id: customerId, category, summary, evidence_refs: [] },
            }))
          }
        >
          {t('action.create')}
        </button>
      </Card>
    </>
  );
}
